import * as React from "react";
import * as SwitchPrimitives from "@radix-ui/react-switch";
import { cn } from "../lib/cn";

/**
 * Switch — controlled boolean toggle.
 *
 * Wraps Radix's Switch.Root + Switch.Thumb with token-driven styling
 * (`data-[state=checked]:bg-primary`, `data-[state=unchecked]:bg-input`),
 * matching the rest of pretty's interactive primitives. Pair with
 * `<Label>` for the field caption.
 *
 *   <div className="flex items-center gap-3">
 *     <Switch id="notifications" checked={on} onCheckedChange={setOn} />
 *     <Label htmlFor="notifications">Push notifications</Label>
 *   </div>
 *
 * Pretty's defaults match the canonical shadcn-style toggle (h-6 w-11
 * track, h-5 w-5 thumb, 5px thumb travel) so no consumer override is
 * needed for the common case. Pass `size="sm"` for dense surfaces
 * (toolbars, table rows).
 */
const SWITCH_SIZES = {
  default: { track: "h-6 w-11", thumb: "h-5 w-5 data-[state=checked]:translate-x-5" },
  sm: { track: "h-5 w-9", thumb: "h-4 w-4 data-[state=checked]:translate-x-4" },
} as const;

export type SwitchProps = React.ComponentPropsWithoutRef<typeof SwitchPrimitives.Root> & {
  /** Track/thumb scale. Defaults to `"default"`. */
  size?: keyof typeof SWITCH_SIZES;
};

export const Switch = React.forwardRef<
  React.ElementRef<typeof SwitchPrimitives.Root>,
  SwitchProps
>(({ className, size = "default", ...props }, ref) => {
  const sizing = SWITCH_SIZES[size];
  return (
    <SwitchPrimitives.Root
      ref={ref}
      className={cn(
        "peer inline-flex shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent",
        sizing.track,
        "transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        "disabled:cursor-not-allowed disabled:opacity-50",
        "data-[state=checked]:bg-primary data-[state=unchecked]:bg-input",
        className,
      )}
      {...props}
    >
      <SwitchPrimitives.Thumb
        className={cn(
          "pointer-events-none block rounded-full bg-background shadow-lg ring-0",
          sizing.thumb,
          "transition-transform",
          "data-[state=unchecked]:translate-x-0",
        )}
      />
    </SwitchPrimitives.Root>
  );
});
Switch.displayName = "Switch";
